import {PlanTypeLimits, PlanTypeRealEstateType} from '../constants/index'
export function formatDateDtAPIPost(date: Date) {
    return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`
}

export function formatDateDt(date: Date) {
    return `${date.getFullYear()}/${pad2(date.getMonth() + 1)}/${pad2(date.getDate())}`
}

export function formatDate(dateStr: string) {
    if (!dateStr) return ''
    const date = new Date(dateStr)
    return formatDateDt(date)
}

export function pad2(number: number) {
    return String(number).padStart(2, '0')
}
export function pad5(number: number) {
    return String(number).padStart(5, '0')
}

export function isStringArraysEqual(arr1: string[] | undefined, arr2: string[] | undefined) {
    if (!arr1 || !arr2) return false

    const arr1Str = arr1.join('')
    const arr2Str = arr2.join('')
    return arr1Str == arr2Str
}

export function readURIFromFile(file: File){
    return new Promise((resolve, reject) => {
        let reader = new FileReader();
        reader.onload = function(ev: any){
            resolve(ev.target.result)
        }
        reader.readAsDataURL(file);
    })
}

export function generateThumbnail(file: File, maxWidth: number = 168, maxHeight: number = 120): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        
        reader.onload = (e) => {
            const img = new Image();
            
            img.onload = () => {
                // キャンバスを作成
                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d');
                
                if (!ctx) {
                    reject(new Error('Failed to get canvas context'));
                    return;
                }
                
                // アスペクト比を保持しながらサイズを計算
                const scale = Math.min(maxWidth / img.width, maxHeight / img.height);
                const width = img.width * scale;
                const height = img.height * scale;
                
                // キャンバスのサイズを設定
                canvas.width = maxWidth;
                canvas.height = maxHeight;
                
                // 背景を白で塗りつぶし
                ctx.fillStyle = '#ffffff';
                ctx.fillRect(0, 0, maxWidth, maxHeight);
                
                // 画像を中央に配置
                const x = (maxWidth - width) / 2;
                const y = (maxHeight - height) / 2;
                
                // 画像を描画
                ctx.imageSmoothingEnabled = true;
                ctx.imageSmoothingQuality = 'high';
                ctx.drawImage(img, x, y, width, height);
                
                // JPEGとして出力（品質0.9で高画質）
                canvas.toBlob((blob) => {
                    if (blob) {
                        const reader = new FileReader();
                        reader.onload = () => {
                            // メモリリーク対策：Canvas要素をクリア
                            canvas.width = 0;
                            canvas.height = 0;
                            resolve(reader.result as string);
                        };
                        reader.onerror = () => {
                            // エラー時もCanvas要素をクリア
                            canvas.width = 0;
                            canvas.height = 0;
                            reject(new Error('Failed to read thumbnail blob'));
                        };
                        reader.readAsDataURL(blob);
                    } else {
                        // エラー時もCanvas要素をクリア
                        canvas.width = 0;
                        canvas.height = 0;
                        reject(new Error('Failed to create thumbnail blob'));
                    }
                }, 'image/jpeg', 0.9);
            };
            
            img.onerror = () => {
                reject(new Error('Failed to load image'));
            };
            
            img.src = e.target?.result as string;
        };
        
        reader.onerror = () => {
            reject(new Error('Failed to read file'));
        };
        
        reader.readAsDataURL(file);
    });
}

export function getRandId() {
    return Math.floor(100000000 + Math.random() * 900000000);
}

export function getKeyByValue(object: any, value: any) {
    return Object.keys(object).find(key => object[key] === value);
}

export function checkPostCodeFormat(zipCode: string): Boolean {
    if (zipCode.trim().length == 0) {
        return true
    }

    return /(^\d{5}$)|(^\d{5}-\d{4}$)|(^\d{4}-\d{4}$)|(^\d{3}-\d{4}$)/.test(zipCode)
}

interface Account {
    contract_end_date: string,
    credit_consumed: number,
    credit_limit: number,
}

// ユーザーステータス判定関数（3つのステータスを返す）
export function isExpiredUser({contract_end_date, credit_consumed, credit_limit} : Account, user?: {is_active: boolean}): string {
    // 1. is_active=Falseの場合は「退会」
    if (user && !user.is_active) {
        return '退会'
    }

    // 2. 契約終了日を超えている場合は「退会」
    if (contract_end_date) {
        const toDay = new Date();
        toDay.setHours(0,0,0,0);
        const contractDate = new Date(contract_end_date);
        if (contractDate.getTime() < toDay.getTime()) {
            return '退会'
        }
    }

    // 3. クレジット上限に達している場合は「停止」
    if (credit_consumed >= credit_limit) {
        return '停止'
    }

    // 4. それ以外は「利用中」
    return '利用中'
}

// アカウントステータス判定関数
export function isExpiredAccount({
  is_active,
  contract_end_date,
  credit_consumed,
  credit_limit,
}: Account): string {
  // 1. is_active=Falseの場合は「退会」
  if (is_active === false) {
    return "退会";
  }

  // 2. 契約終了日を超えている場合は「退会」
  if (contract_end_date) {
    const toDay = new Date();
    toDay.setHours(0, 0, 0, 0);
    const contractDate = new Date(contract_end_date);
    if (contractDate.getTime() < toDay.getTime()) {
      return "退会";
    }
  }

  // 3. クレジット上限に達している場合は「停止」
  if (credit_consumed >= credit_limit) {
    return "停止";
  }

  // 4. それ以外は「利用中」
  return "利用中";
}

// 後方互換性のために boolean を返すバージョンも提供
export function getUserStatusBoolean({contract_end_date, credit_consumed, credit_limit} : Account): boolean {
    if (!contract_end_date) {
        return false
    }

    if (credit_consumed >= credit_limit) {
        return true
    }

    const toDay = new Date();
    toDay.setHours(0,0,0,0);
    const contractDate = new Date(contract_end_date);
    return contractDate.getTime() < toDay.getTime()
}

export function calPlanLimits(realEstateName: string, planId: string) {
    if (!PlanTypeLimits[realEstateName]) {
        return ''
    }
    const PlanTypeFlipped = Object.entries(PlanTypeRealEstateType[realEstateName]).map(([key, value]) => [value, key]);
    const planDetail = Object.fromEntries(PlanTypeFlipped)
    if (!planDetail[planId]) {
        return ''
    }

    const planeName = planDetail[planId]
    //['Distribution]['Pro'] = 300
    return PlanTypeLimits[realEstateName][planeName]
}
